/** Recognize transient provider failures even when viem wraps them in contract errors. */
export function isRetryableRpcError(error: unknown): boolean {
  const seen = new Set<unknown>();
  for (let cause = error; cause && typeof cause === 'object' && !seen.has(cause);) {
    seen.add(cause);
    const entry = cause as Record<string, unknown>;
    if ([408, 429, 500, 502, 503, 504].includes(Number(entry.status)) || entry.code === -32005) return true;
    const text = [entry.name, entry.shortMessage, entry.message, entry.details].join(' ');
    if (/rate.?limit|too many requests|\b429\b|\b1015\b|timed?\s*out|timeout|network error|failed to fetch|fetch failed|socket hang up|ECONNRESET|ETIMEDOUT/i.test(text)) return true;
    cause = entry.cause;
  }
  return false;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal!.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Used by the sequential grant reader: pace requests and bound transient retries. */
export function createRpcReader(signal?: AbortSignal) {
  let nextReadAt = 0;
  return async function read<T>(request: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      signal?.throwIfAborted();
      const delay = Math.max(0, nextReadAt - Date.now());
      if (delay > 0) await wait(delay, signal);
      signal?.throwIfAborted();
      try {
        const result = await request();
        signal?.throwIfAborted();
        nextReadAt = Date.now() + 200;
        return result;
      } catch (error) {
        signal?.throwIfAborted();
        nextReadAt = Date.now() + 200;
        if (attempt >= 2 || !isRetryableRpcError(error)) throw error;
        // 500ms, then 1000ms. Navigation aborts this wait immediately.
        nextReadAt = Date.now() + 500 * 2 ** attempt;
      }
    }
  };
}
